import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/ui/RichTextEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import ThemeTourTable from './ThemeTourTable';
import {
  Plus,
  Trash2,
  Search,
  Compass,
  Upload,
  Link2,
  ExternalLink,
  Copy,
  Check,
  X,
  Calendar,
  GripVertical,
  RefreshCw,
  Sparkles,
  Wand2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminThemeTours = () => {
  const { getAuthHeaders } = useAdmin();
  const [tours, setTours] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [themeTypes, setThemeTypes] = useState([]);
  const [themeCategories, setThemeCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTheme, setFilterTheme] = useState('all');
  
  // Country/City/State selection
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citySearchOpen, setCitySearchOpen] = useState(false);
  
  // AI Itinerary Builder
  const [generatingItinerary, setGeneratingItinerary] = useState(false);
  const [itinerarySkeleton, setItinerarySkeleton] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdTour, setCreatedTour] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  
  // Image upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  // New theme type and category
  const [newThemeType, setNewThemeType] = useState('');
  const [showAddTheme, setShowAddTheme] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#ef4444');
  const [addingCategory, setAddingCategory] = useState(false);
  
  // Default Terms & Conditions (same as packages)
  const defaultTermsConditions = `Booking Confirmation – A trip booking is considered confirmed only after the required deposit or full payment is received and acknowledged by the company.
Payment Terms – The remaining balance must be paid before the travel date as per the payment schedule communicated at the time of booking. Failure to complete payment within the given timeline may result in cancellation of the booking.
Pricing & Rate Changes – Package prices are subject to availability and may change due to fluctuations in airfare, hotel pricing, currency exchange rates, fuel surcharges, government taxes, or supplier costs until the booking is confirmed.
Third-Party Services – Hotels, airlines, cruise operators, transport providers, and activity organizers are independent service providers. The company acts only as a booking facilitator and is not responsible for their operational decisions, service quality, delays, or changes.
Itinerary Changes – The itinerary may be adjusted due to weather conditions, operational requirements, local regulations, or unforeseen circumstances. In such cases, reasonable alternatives will be arranged where possible.
Travel Documents – Travelers are responsible for ensuring that they carry valid passports, visas, permits, and identification documents required for the trip. Any issues arising due to incomplete or invalid documentation will be the traveler's responsibility.
Visa Assistance – Visa support or documentation guidance may be provided where applicable; however, visa approval is solely at the discretion of the respective embassy or consulate. The company cannot guarantee visa approval.
Flights & Transportation – Flight schedules, baggage policies, and transportation services are governed by the respective airline or transport provider. Any schedule changes, cancellations, or delays are beyond the company's control.
Hotel Policies – Hotel room allocation, check-in and check-out timings, and property facilities are subject to the policies of the respective hotel. Early check-in or late check-out is subject to availability.
Cancellations & Refunds – Cancellation charges may apply as per the cancellation policy communicated at the time of booking. Refund timelines depend on airlines, hotels, and other service providers involved in the booking.
Travel Insurance – Travelers are strongly advised to obtain comprehensive travel insurance covering medical emergencies, trip cancellations, delays, accidents, and baggage loss.
Traveler Conduct – Travelers are expected to behave responsibly and follow local laws and regulations. The company reserves the right to remove any traveler from a trip without refund if their behavior disrupts the safety or experience of others.
Personal Responsibility – Travelers are responsible for their personal belongings, health conditions, and participation in activities during the trip. The company will not be responsible for any personal loss, injury, or damage during travel.
Force Majeure – The company shall not be liable for disruptions caused by events beyond its control, including but not limited to natural disasters, political unrest, strikes, pandemics, government restrictions, or airline disruptions.
Limitation of Liability – The company's liability is limited to the amount paid for the travel services booked through the company and does not extend to indirect or consequential losses.
Jurisdiction – Any disputes arising from bookings or travel services shall be subject to the jurisdiction of the courts in the city where the company is registered.
Acceptance of Terms – By confirming a booking with the company, travelers acknowledge that they have read, understood, and accepted these terms and conditions.`;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    country: '',
    city: '',
    destination: '',
    duration: '',
    price: '',
    category: 'domestic',
    theme: '',
    theme_category: '',
    image: '',
    highlights: '',
    inclusions: '',
    exclusions: '',
    terms_conditions: defaultTermsConditions,
    itinerary: [],
    is_active: true
  });

  const fetchData = useCallback(async () => {
    try {
      const [toursRes, destinationsRes, themesRes, categoriesRes, countriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/theme-tours`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/destinations`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/theme-types`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/theme-tour-categories`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/destinations/countries`, { headers: getAuthHeaders() })
      ]);
      setTours(toursRes.data);
      setDestinations(destinationsRes.data);
      setThemeTypes(themesRes.data.themes || []);
      setThemeCategories(categoriesRes.data || []);
      setCountries(countriesRes.data || []);
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Failed to fetch theme tours');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCitiesForCountry = async (countryName) => {
    if (!countryName) {
      setCities([]);
      setStates([]);
      return null;
    }
    
    setLoadingCities(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/destinations/cities/${encodeURIComponent(countryName)}`,
        { headers: getAuthHeaders() }
      );
      setCities(response.data.cities || []);
      setStates(response.data.states || []);
      return response.data.category || 'international';
    } catch (error) {
      logger.error('Error fetching cities:', error);
      setCities([]);
      setStates([]);
      return 'international';
    } finally {
      setLoadingCities(false);
    }
  };

  const handleCountryChange = async (countryName) => {
    const category = await fetchCitiesForCountry(countryName);
    setFormData(prev => ({
      ...prev,
      country: countryName,
      city: '',
      destination: countryName,
      category: category || 'international'
    }));
  };

  const handleCityChange = (cityName) => {
    const actualCity = cityName === '__none__' ? '' : cityName;
    const destinationName = actualCity || formData.country;
    setFormData(prev => ({
      ...prev,
      city: actualCity,
      destination: destinationName
    }));
  };

  const handleAddNewCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    setAddingCategory(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/theme-tour-categories`,
        {
          name: newCategoryName.trim(),
          icon: newCategoryIcon || '🎯',
          color: newCategoryColor,
          is_active: true
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Category "${newCategoryName}" created`);
      
      // Refresh categories
      const categoriesRes = await axios.get(
        `${API_URL}/api/admin/theme-tour-categories`,
        { headers: getAuthHeaders() }
      );
      setThemeCategories(categoriesRes.data || []);
      
      // Auto-select the new category
      setFormData(prev => ({ ...prev, theme_category: response.data.id }));
      
      // Reset form
      setNewCategoryName('');
      setNewCategoryIcon('');
      setNewCategoryColor('#ef4444');
      setShowAddCategory(false);
    } catch (error) {
      logger.error('Error adding category:', error);
      toast.error(error.response?.data?.detail || 'Failed to add category');
    } finally {
      setAddingCategory(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      country: '',
      city: '',
      destination: '',
      duration: '',
      price: '',
      category: 'domestic',
      theme: '',
      theme_category: '',
      image: '',
      highlights: '',
      inclusions: '',
      exclusions: '',
      terms_conditions: defaultTermsConditions,
      itinerary: [],
      is_active: true
    });
    setCities([]);
    setStates([]);
    setEditingTour(null);
  };

  // Duration formatting
  const formatDuration = (nights) => {
    const n = parseInt(nights);
    if (isNaN(n) || n < 1) return '';
    return `${n} Night${n > 1 ? 's' : ''} / ${n + 1} Day${n + 1 > 1 ? 's' : ''}`;
  };

  const handleDurationChange = (value) => {
    // Extract number from input
    const numericValue = value.replace(/[^0-9]/g, '');
    setFormData({ ...formData, duration: numericValue });
  };

  // AI Itinerary Generation
  const generateAIItinerary = async () => {
    if (!formData.country) {
      toast.error('Please select a country first');
      return;
    }
    
    if (!formData.duration) {
      toast.error('Please enter the number of nights first');
      return;
    }
    
    const nights = parseInt(formData.duration);
    if (isNaN(nights) || nights < 1) {
      toast.error('Please enter a valid number of nights');
      return;
    }
    
    setGeneratingItinerary(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/packages/generate-itinerary`,
        {
          country: formData.country,
          city: formData.city || null,
          nights: nights,
          skeleton: itinerarySkeleton || null
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        // Set generated package title suggestion
        if (response.data.package_title && !formData.title) {
          let title = response.data.package_title;
          if (typeof title === 'string' && title.startsWith('"') && title.endsWith('"')) {
            title = title.slice(1, -1);
          }
          setFormData(prev => ({ ...prev, title }));
        }
        
        // Process itinerary
        const generatedItinerary = (response.data.itinerary || []).map((day, idx) => ({
          day: idx + 1,
          title: typeof day.title === 'string' ? day.title : String(day.title || ''),
          description: typeof day.description === 'string' ? day.description : String(day.description || ''),
          activitiesInput: Array.isArray(day.activities) ? day.activities.join(', ') : ''
        }));
        
        setFormData(prev => ({ ...prev, itinerary: generatedItinerary }));
        toast.success(`Generated ${generatedItinerary.length}-day itinerary!`);
        setItinerarySkeleton('');
      } else {
        toast.error(response.data.error || 'Failed to generate itinerary');
      }
    } catch (error) {
      logger.error('AI Generation error:', error);
      toast.error('Failed to generate itinerary');
    } finally {
      setGeneratingItinerary(false);
    }
  };

  // Itinerary functions
  const addItineraryDay = () => {
    const newDay = {
      day: formData.itinerary.length + 1,
      title: '',
      description: '',
      activitiesInput: ''
    };
    setFormData({ ...formData, itinerary: [...formData.itinerary, newDay] });
  };

  const updateItineraryDay = (index, field, value) => {
    const updated = [...formData.itinerary];
    updated[index][field] = value;
    setFormData({ ...formData, itinerary: updated });
  };

  const removeItineraryDay = (index) => {
    const updated = formData.itinerary.filter((_, i) => i !== index);
    updated.forEach((day, i) => { day.day = i + 1; });
    setFormData({ ...formData, itinerary: updated });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = async (tour) => {
    setEditingTour(tour);
    
    const itineraryWithInputs = (tour.itinerary || []).map(day => ({
      ...day,
      activitiesInput: (day.activities || []).join(', ')
    }));
    
    // Get country - prefer stored country field, fallback to destination
    let tourCountry = tour.country || '';
    let tourCity = tour.city || '';
    
    // If no country stored, try to find from destinations or country list
    if (!tourCountry && tour.destination) {
      // Check if destination matches a country name
      const matchedCountry = countries.find(c => 
        c.name.toLowerCase() === tour.destination.toLowerCase()
      );
      if (matchedCountry) {
        tourCountry = matchedCountry.name;
      } else {
        // Maybe destination is a city - find which country it belongs to
        const destLower = tour.destination.toLowerCase();
        for (const dest of destinations) {
          if (dest.name.toLowerCase() === destLower) {
            tourCountry = dest.country || '';
            tourCity = dest.name;
            break;
          }
        }
      }
    }
    
    // Extract number of nights from duration string (e.g., "5 Nights / 6 Days" -> "5")
    let durationNights = tour.duration || '';
    const nightMatch = durationNights.match(/(\d+)\s*night/i);
    if (nightMatch) {
      durationNights = nightMatch[1];
    }
    
    // Fetch cities/states for the country FIRST
    if (tourCountry) {
      await fetchCitiesForCountry(tourCountry);
    }
    
    // Now set form data after cities are loaded
    setFormData({
      title: tour.title || '',
      country: tourCountry,
      city: tourCity,
      destination: tour.destination || '',
      duration: durationNights,
      price: tour.price?.toString() || '',
      category: tour.category || 'international',
      theme: tour.theme || '',
      theme_category: tour.theme_category || '',
      image: tour.image || '',
      highlights: tour.highlights?.join(', ') || '',
      inclusions: tour.inclusions?.join(', ') || '',
      exclusions: tour.exclusions?.join(', ') || '',
      terms_conditions: tour.terms_conditions?.join('\n') || '',
      itinerary: itineraryWithInputs,
      is_active: tour.is_active !== false
    });
    
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      const ext = file.name.split('.').pop() || 'unknown';
      toast.error(`Unsupported format (.${ext}). Use JPG, PNG, WebP, or GIF`);
      e.target.value = '';
      return;
    }
    if (file.size > maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`File too large (${sizeMB} MB). Maximum is 10 MB`);
      e.target.value = '';
      return;
    }
    if (file.size === 0) {
      toast.error('File is empty (0 bytes)');
      e.target.value = '';
      return;
    }
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload-image`,
        formDataUpload,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      const imageUrl = `${API_URL}${response.data.url}`;
      setFormData({ ...formData, image: imageUrl });
      toast.success('Image uploaded successfully');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed. Please try again.';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleAddThemeType = async () => {
    if (!newThemeType.trim()) return;
    
    try {
      await axios.post(
        `${API_URL}/api/admin/theme-types?theme_name=${encodeURIComponent(newThemeType)}`,
        {},
        { headers: getAuthHeaders() }
      );
      setThemeTypes([...themeTypes, newThemeType.toLowerCase()]);
      setNewThemeType('');
      setShowAddTheme(false);
      toast.success('Theme type added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add theme type');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.theme_category) {
      toast.error('Please select a category');
      return;
    }
    
    const processedItinerary = formData.itinerary.map(day => ({
      day: day.day,
      title: day.title,
      description: day.description,
      activities: (day.activitiesInput || '').split(',').map(s => s.trim()).filter(Boolean)
    }));
    
    // Format duration
    const formattedDuration = formatDuration(formData.duration) || formData.duration;
    
    const payload = {
      title: formData.title,
      country: formData.country,
      city: formData.city,
      destination: formData.destination || formData.city || formData.country,
      duration: formattedDuration,
      price: parseInt(formData.price),
      category: formData.category,
      theme: formData.theme,
      theme_category: formData.theme_category || null,
      image: formData.image,
      highlights: formData.highlights.split(',').map(s => s.trim()).filter(Boolean),
      inclusions: formData.inclusions.split(',').map(s => s.trim()).filter(Boolean),
      exclusions: formData.exclusions.split(',').map(s => s.trim()).filter(Boolean),
      terms_conditions: formData.terms_conditions.split('\n').map(s => s.trim()).filter(Boolean),
      itinerary: processedItinerary,
      is_active: formData.is_active
    };

    try {
      if (editingTour) {
        await axios.put(
          `${API_URL}/api/admin/theme-tours/${editingTour.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Theme tour updated successfully');
        setShowModal(false);
      } else {
        const response = await axios.post(
          `${API_URL}/api/admin/theme-tours`,
          payload,
          { headers: getAuthHeaders() }
        );
        setCreatedTour(response.data);
        setShowModal(false);
        setShowSuccessModal(true);
      }
      resetForm();
      fetchData();
    } catch (error) {
      logger.error('Error saving theme tour:', error);
      toast.error(error.response?.data?.detail || 'Failed to save theme tour');
    }
  };

  const handleDelete = async (tourId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/theme-tours/${tourId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Theme tour deleted successfully');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      logger.error('Error deleting theme tour:', error);
      toast.error('Failed to delete theme tour');
    }
  };

  const toggleTourActive = async (tour) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/theme-tours/${tour.id}`,
        { is_active: !tour.is_active },
        { headers: getAuthHeaders() }
      );
      toast.success(`Theme tour ${!tour.is_active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update theme tour status');
    }
  };

  const getTourUrl = (tour) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/theme-tours/${tour.tour_code}`;
  };

  const copyTourLink = (tour) => {
    navigator.clipboard.writeText(getTourUrl(tour));
    setCopiedLink(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const openTourInNewTab = (tour) => {
    window.open(getTourUrl(tour), '_blank');
  };

  const filteredTours = tours.filter(tour => {
    const matchesSearch = tour.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tour.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tour.tour_code?.includes(searchTerm);
    const matchesCategory = filterCategory === 'all' || tour.category === filterCategory;
    const matchesTheme = filterTheme === 'all' || tour.theme === filterTheme;
    return matchesSearch && matchesCategory && matchesTheme;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="admin-theme-tours">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Compass className="h-5 w-5 lg:h-6 lg:w-6 text-red-500" />
            Theme Tours
          </h1>
          <p className="text-sm lg:text-base text-slate-500">Manage themed travel experiences</p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
          data-testid="add-theme-tour-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Theme Tour
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 lg:p-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, destination, or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-theme-tours"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full lg:w-40" data-testid="filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="international">International</SelectItem>
                  <SelectItem value="domestic">Domestic</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterTheme} onValueChange={setFilterTheme}>
                <SelectTrigger className="w-full lg:w-40" data-testid="filter-theme">
                  <SelectValue placeholder="Theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Themes</SelectItem>
                  {themeTypes.map(theme => (
                    <SelectItem key={theme} value={theme} className="capitalize">{theme}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tours List */}
      <ThemeTourTable
        filteredTours={filteredTours}
        openEditModal={openEditModal}
        setDeleteConfirm={setDeleteConfirm}
        toggleTourActive={toggleTourActive}
        copyTourLink={copyTourLink}
        openTourInNewTab={openTourInNewTab}
      />

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTour ? 'Edit Theme Tour' : 'Create New Theme Tour'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Tour Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="input-title"
                />
              </div>
              
              {/* Country Selection */}
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select
                  value={formData.country}
                  onValueChange={handleCountryChange}
                >
                  <SelectTrigger data-testid="input-country">
                    <SelectValue placeholder="Select country..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectGroup>
                      <SelectLabel className="text-green-600 bg-green-50 -mx-1 px-3">Domestic</SelectLabel>
                      {countries.filter(c => c.category === 'domestic').map(country => (
                        <SelectItem key={country.code} value={country.name}>
                          🇮🇳 {country.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel className="text-blue-600 bg-blue-50 -mx-1 px-3">International</SelectLabel>
                      {countries.filter(c => c.category === 'international').map(country => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {formData.country && (
                  <p className={`text-xs px-2 py-1 rounded ${
                    formData.category === 'domestic' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'
                  }`}>
                    Auto-tagged as <strong>{formData.category}</strong>
                  </p>
                )}
              </div>
              
              {/* City/State Selection - Searchable */}
              <div className="space-y-2">
                <Label htmlFor="city">
                  City/State/Province
                  <span className="text-xs text-slate-400 font-normal ml-1">(Type to search)</span>
                </Label>
                {formData.country ? (
                  loadingCities ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading locations...
                    </div>
                  ) : (cities.length > 0 || states.length > 0) ? (
                    <Popover open={citySearchOpen} onOpenChange={setCitySearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={citySearchOpen}
                          className="w-full justify-between font-normal"
                          data-testid="input-city"
                        >
                          {formData.city ? (
                            <span className="flex items-center gap-2">
                              {states.includes(formData.city) ? '📍' : '🏙️'} {formData.city}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Search city/state/province...</span>
                          )}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Type to search..." />
                          <CommandList className="max-h-[300px]">
                            <CommandEmpty>No location found.</CommandEmpty>
                            {formData.city && (
                              <CommandItem
                                onSelect={() => {
                                  handleCityChange('');
                                  setCitySearchOpen(false);
                                }}
                                className="text-slate-500"
                              >
                                ✕ Clear selection
                              </CommandItem>
                            )}
                            {states.length > 0 && (
                              <CommandGroup heading="States / Provinces">
                                {states.map(state => (
                                  <CommandItem
                                    key={`state-${state}`}
                                    value={state}
                                    onSelect={() => {
                                      handleCityChange(state);
                                      setCitySearchOpen(false);
                                    }}
                                  >
                                    <span className={formData.city === state ? "font-semibold" : ""}>
                                      📍 {state}
                                    </span>
                                    {formData.city === state && <Check className="ml-auto h-4 w-4" />}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                            {cities.length > 0 && (
                              <CommandGroup heading="Cities">
                                {cities.map(city => (
                                  <CommandItem
                                    key={`city-${city}`}
                                    value={city}
                                    onSelect={() => {
                                      handleCityChange(city);
                                      setCitySearchOpen(false);
                                    }}
                                  >
                                    <span className={formData.city === city ? "font-semibold" : ""}>
                                      🏙️ {city}
                                    </span>
                                    {formData.city === city && <Check className="ml-auto h-4 w-4" />}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Input
                      value={formData.city}
                      onChange={(e) => handleCityChange(e.target.value)}
                      placeholder="Enter city/state name..."
                      data-testid="input-city"
                    />
                  )
                ) : (
                  <p className="text-sm text-slate-400 py-2">Select a country first</p>
                )}
              </div>
              
              {/* Duration with formatting */}
              <div className="space-y-2">
                <Label htmlFor="duration">Duration * <span className="text-xs text-slate-400 font-normal">(Enter number of nights)</span></Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="duration"
                    type="text"
                    placeholder="e.g., 4"
                    value={formData.duration}
                    onChange={(e) => handleDurationChange(e.target.value)}
                    className="w-20"
                    maxLength={2}
                    required
                    data-testid="input-duration"
                  />
                  <span className="text-sm text-slate-500">nights</span>
                </div>
                {formData.duration && (
                  <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                    Preview: {formatDuration(formData.duration)}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price (₹) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  data-testid="input-price"
                />
              </div>
            </div>

            {/* Tour Category Selection (from Tour Categories page only) */}
            <div className="space-y-2">
              <Label>Theme / Category *</Label>
              <p className="text-xs text-slate-500 mb-2">Select a category added via Tour Categories</p>
              
              <div className="flex flex-wrap gap-2">
                {themeCategories.filter(c => c.is_active).map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, theme: cat.name.toLowerCase(), theme_category: cat.id })}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      formData.theme_category === cat.id
                        ? 'text-white border-transparent'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                    }`}
                    style={formData.theme_category === cat.id ? { backgroundColor: cat.color } : {}}
                  >
                    {cat.icon && <span className="mr-1">{cat.icon}</span>}
                    {formData.theme_category === cat.id && <Check size={14} className="inline mr-1" />}
                    {cat.name}
                  </button>
                ))}
              </div>
              {themeCategories.filter(c => c.is_active).length === 0 && (
                <p className="text-xs text-amber-600">No categories found. Add categories from the Tour Categories page first.</p>
              )}
              {!formData.theme_category && themeCategories.filter(c => c.is_active).length > 0 && (
                <p className="text-xs text-red-500">Please select a category</p>
              )}
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Tour Image *</Label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="Image URL or upload from system"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="flex-1"
                  data-testid="input-image"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                  ) : (
                    <><Upload size={16} className="mr-2" /> Upload</>
                  )}
                </Button>
              </div>
              {formData.image && (
                <img src={formData.image} alt="Preview" className="h-24 w-auto rounded-lg object-cover mt-2" />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="highlights">Highlights (comma-separated)</Label>
              <Textarea
                id="highlights"
                placeholder="Wildlife Safari, Jungle Stay, Nature Trails..."
                value={formData.highlights}
                onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData('text');
                  // Normalize newlines, tabs, pipes to commas
                  const normalized = pasted.replace(/[\n\r\t|]+/g, ', ').replace(/,\s*,/g, ',').replace(/^[,\s]+|[,\s]+$/g, '');
                  const textarea = e.target;
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const current = formData.highlights;
                  const newValue = current.substring(0, start) + normalized + current.substring(end);
                  setFormData({ ...formData, highlights: newValue });
                }}
                data-testid="input-highlights"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inclusions">Inclusions (comma-separated)</Label>
              <Textarea
                id="inclusions"
                placeholder="Accommodation, Guide, Activities..."
                value={formData.inclusions}
                onChange={(e) => setFormData({ ...formData, inclusions: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="exclusions">Exclusions (comma-separated)</Label>
              <Textarea
                id="exclusions"
                placeholder="Flights, Personal expenses..."
                value={formData.exclusions}
                onChange={(e) => setFormData({ ...formData, exclusions: e.target.value })}
              />
            </div>

            {/* AI Itinerary Builder */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <Label className="text-base font-semibold">AI Itinerary Builder</Label>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Powered by AI</span>
              </div>
              <p className="text-sm text-slate-500">
                Let AI generate a complete day-wise itinerary based on your destination and duration
              </p>
              
              <div className="space-y-3 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                <div className="space-y-2">
                  <Label className="text-sm">Itinerary Skeleton (Optional)</Label>
                  <Textarea
                    placeholder="E.g.: Day 1 - Arrival, Day 2 - City Tour, Day 3 - Beach, Day 4 - Adventure Sports..."
                    value={itinerarySkeleton}
                    onChange={(e) => setItinerarySkeleton(e.target.value)}
                    rows={2}
                    className="bg-white/80"
                  />
                  <p className="text-xs text-slate-500">
                    Provide a rough outline and AI will flesh out the details, or leave empty for full AI generation
                  </p>
                </div>
                
                <Button
                  type="button"
                  onClick={generateAIItinerary}
                  disabled={generatingItinerary || !formData.country || !formData.duration}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  {generatingItinerary ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Generating Itinerary...
                    </>
                  ) : (
                    <>
                      <Wand2 className="mr-2 h-4 w-4" />
                      Generate AI Itinerary
                    </>
                  )}
                </Button>
                
                {(!formData.country || !formData.duration) && (
                  <p className="text-xs text-amber-600">
                    Select a country and enter duration to enable AI generation
                  </p>
                )}
              </div>
            </div>

            {/* Itinerary Builder */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Calendar size={18} /> Day-wise Itinerary
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItineraryDay}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Plus size={14} className="mr-1" /> Add Day
                </Button>
              </div>
              
              {formData.itinerary.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Calendar className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">No itinerary added yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.itinerary.map((day, index) => (
                    <div key={`day-${day.day}-${index}`} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <GripVertical size={16} className="text-slate-400" />
                          <span className="font-semibold text-red-600">Day {day.day}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItineraryDay(index)}
                          className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Day Title</Label>
                          <Input
                            placeholder="e.g., Arrival in Safari Lodge"
                            value={day.title}
                            onChange={(e) => updateItineraryDay(index, 'title', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Activities (comma-separated)</Label>
                          <Input
                            placeholder="Morning Safari, Evening Bonfire"
                            value={day.activitiesInput || ''}
                            onChange={(e) => updateItineraryDay(index, 'activitiesInput', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs text-slate-500 mb-1 block">Description</Label>
                        <RichTextEditor
                          value={day.description || ''}
                          onChange={(value) => updateItineraryDay(index, 'description', value)}
                          placeholder="Detailed description of the day..."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="terms">Terms & Conditions (one per line)</Label>
              <Textarea
                id="terms"
                placeholder="50% advance payment required&#10;Cancellation charges apply"
                value={formData.terms_conditions}
                onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <div>
                <Label htmlFor="is_active" className="font-medium">Show on Website</Label>
                <p className="text-xs text-slate-500">
                  {formData.is_active ? 'Tour will be visible in website listings' : 'Tour will be hidden from listings'}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-theme-tour-btn">
                {editingTour ? 'Update Theme Tour' : 'Create Theme Tour'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-6 h-6" /> Theme Tour Created!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">
              Your theme tour <strong>{createdTour?.title}</strong> has been created with code{' '}
              <span className="font-mono bg-slate-100 px-2 py-1 rounded">#{createdTour?.tour_code}</span>
            </p>
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Link2 className="w-5 h-5 text-slate-400" />
              <Input value={createdTour ? getTourUrl(createdTour) : ''} readOnly className="flex-1 bg-white" />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" variant="outline" onClick={() => createdTour && copyTourLink(createdTour)}>
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => createdTour && openTourInNewTab(createdTour)}>
                <ExternalLink className="w-4 h-4 mr-2" /> Open
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSuccessModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Theme Tour</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete <strong>{deleteConfirm?.title}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(deleteConfirm?.id)}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminThemeTours;
